import type { FloorObject } from '../dto/floor-object.dto.js'

/** A placed object, as far as counting it against stock needs one. */
export interface StockCounted {
  type: FloorObject['type']
  sourceItemId?: string | undefined
  units?: number | undefined
}

/**
 * How many of its catalog item one placed piece uses.
 *
 * A riser runway is several units of one riser laid end to end, and a venue
 * stocks the units — so a six-unit runway uses six, and every count of placed
 * furniture against stock adds this rather than one. Every other piece is one.
 */
export function stockUnits(object: StockCounted): number {
  return object.type === 'furniture' && object.units !== undefined && object.units > 0 ? object.units : 1
}

/**
 * How many units of each catalog item these objects use, keyed by item id.
 *
 * Every count of a plan against stock starts here, so they all agree on what a
 * runway is. Furniture placed from no item — a client's unlisted piece — is
 * not in it; `furnitureUnits` counts that too.
 */
export function placedUnitsByItem(objects: Iterable<StockCounted>): Map<string, number> {
  const counts = new Map<string, number>()
  for (const object of objects) {
    if (object.type !== 'furniture' || !object.sourceItemId) continue
    counts.set(object.sourceItemId, (counts.get(object.sourceItemId) ?? 0) + stockUnits(object))
  }
  return counts
}

/** How many units of one catalog item these objects use. */
export function placedUnitsOf(objects: Iterable<StockCounted>, itemId: string): number {
  return placedUnitsByItem(objects).get(itemId) ?? 0
}

/** All the furniture these objects are, in units: a plan's piece count, listed or not. */
export function furnitureUnits(objects: Iterable<StockCounted>): number {
  let total = 0
  for (const object of objects) if (object.type === 'furniture') total += stockUnits(object)
  return total
}
